import { z } from 'zod'

import type { DocumentResponse } from './document'
import { TicketAttachmentInputSchema, TicketPriorityEnum } from './ticket'

export const ClientPortalAccessStatuses = [
  'ACTIVE',
  'SUSPENDED',
  'INACTIVE',
] as const
export type ClientPortalAccessStatus =
  (typeof ClientPortalAccessStatuses)[number]

export const ClientPortalInviteStatuses = [
  'PENDING',
  'ACCEPTED',
  'EXPIRED',
  'CANCELLED',
  'DECLINED',
] as const
export type ClientPortalInviteStatus =
  (typeof ClientPortalInviteStatuses)[number]

export const ClientPortalShareModuleTypes = [
  'PROJECT',
  'NOTE',
  'THREAD',
  'DOCUMENT',
  'DOCUMENT_CATEGORY',
] as const
export type ClientPortalShareModuleType =
  (typeof ClientPortalShareModuleTypes)[number]

export const ClientPortalSettingsSchema = z.object({
  id: z.string().uuid().optional(),
  organizationId: z.string().uuid(),
  entityId: z.string().uuid(),
  isEnabled: z.boolean().default(false),
  allowNoteEdits: z.boolean().default(false),
  allowNoteCreate: z.boolean().default(false),
  allowNoteDelete: z.boolean().default(false),
  allowThreadComments: z.boolean().default(false),
  allowThreadReactions: z.boolean().default(false),
  allowNoteFileUploads: z.boolean().default(false),
  allowThreadFileUploads: z.boolean().default(false),
  allowPortalDocumentUploads: z.boolean().default(false),
  allowPortalLinkUploads: z.boolean().default(false),
  allowPortalDocumentDeletes: z.boolean().default(false),
  allowPortalLinkDeletes: z.boolean().default(false),
  allowTicketReplies: z.boolean().default(true),
  allowTicketClose: z.boolean().default(true),
  allowTicketCreate: z.boolean().default(false),
  showNotes: z.boolean().default(true),
  showDocuments: z.boolean().default(true),
  showLinkedDocuments: z.boolean().default(true),
  showDiscussions: z.boolean().default(true),
  showSupport: z.boolean().default(true),
  showDeployments: z.boolean().default(false),
  showRetainers: z.boolean().default(true),
  showRetainerHistory: z.boolean().default(true),
  showRetainerProjectBreakdown: z.boolean().default(true),
  showRetainerMemberBreakdown: z.boolean().default(false),
  showRetainerRoleBreakdown: z.boolean().default(false),
  showRetainerOverageProjection: z.boolean().default(true),
  createdBy: z.string().uuid().nullable().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

export type ClientPortalSettings = z.infer<typeof ClientPortalSettingsSchema>

export const ClientPortalAccessSchema = z.object({
  id: z.string().uuid().optional(),
  organizationId: z.string().uuid(),
  entityId: z.string().uuid(),
  userId: z.string().uuid(),
  status: z.enum(ClientPortalAccessStatuses).default('ACTIVE'),
  invitedBy: z.string().uuid().nullable().optional(),
  inviteId: z.string().uuid().nullable().optional(),
  lastAccessedAt: z.date().nullable().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

export type ClientPortalAccess = z.infer<typeof ClientPortalAccessSchema>

export const ClientPortalInviteSchema = z.object({
  id: z.string().uuid().optional(),
  organizationId: z.string().uuid(),
  entityId: z.string().uuid(),
  email: z.string().email('Invalid email address'),
  name: z.string().nullable().optional(),
  token: z.string().optional(),
  status: z.enum(ClientPortalInviteStatuses).default('PENDING'),
  invitedBy: z.string().uuid(),
  userId: z.string().uuid().nullable().optional(),
  metadata: z.any().nullable().optional(),
  expiresAt: z.date(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

export type ClientPortalInvite = z.infer<typeof ClientPortalInviteSchema>

export const ClientPortalShareSchema = z.object({
  id: z.string().uuid().optional(),
  organizationId: z.string().uuid(),
  entityId: z.string().uuid(),
  moduleType: z.enum(ClientPortalShareModuleTypes),
  moduleId: z.string().uuid(),
  createdBy: z.string().uuid().nullable().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

export type ClientPortalShare = z.infer<typeof ClientPortalShareSchema>

export const CreateClientPortalInviteSchema = z.object({
  email: z.string().email('Invalid email address'),
  name: z.string().nullable().optional(),
  /**
   * Seed the new user's per-user grant. Either pick an org template (resolved
   * + snapshotted server-side at invite time) or pass a custom permission map.
   * `templateId` wins if both are present. Omitting both seeds an empty grant.
   */
  templateId: z.string().uuid().nullable().optional(),
  // `PortalPermissionMapSchema` is declared later in this file; defer with
  // z.lazy to avoid a temporal-dead-zone reference at module load.
  permissions: z.lazy(() => PortalPermissionMapSchema).optional(),
})

export type CreateClientPortalInviteDTO = z.infer<
  typeof CreateClientPortalInviteSchema
>

export const AcceptClientPortalInviteSchema = z.object({
  token: z.string(),
})

export type AcceptClientPortalInviteDTO = z.infer<
  typeof AcceptClientPortalInviteSchema
>

/**
 * Single source of truth for the default client-portal settings when an
 * entity has no row yet. Used by the UI form, the API "no row" fallback,
 * the guard's fallback context, and the magic-link synthesized context.
 */
export const DEFAULT_CLIENT_PORTAL_SETTINGS = {
  isEnabled: false,
  allowNoteEdits: false,
  allowNoteCreate: false,
  allowNoteDelete: false,
  allowThreadComments: false,
  allowThreadReactions: false,
  allowNoteFileUploads: false,
  allowThreadFileUploads: false,
  allowPortalDocumentUploads: false,
  allowPortalLinkUploads: false,
  allowPortalDocumentDeletes: false,
  allowPortalLinkDeletes: false,
  allowTicketReplies: true,
  allowTicketClose: true,
  allowTicketCreate: false,
  showNotes: true,
  showDocuments: true,
  showLinkedDocuments: true,
  showDiscussions: true,
  showSupport: true,
  showDeployments: false,
  showRetainers: true,
  showRetainerHistory: true,
  showRetainerProjectBreakdown: true,
  showRetainerMemberBreakdown: false,
  showRetainerRoleBreakdown: false,
  showRetainerOverageProjection: true,
} as const

export type ClientPortalSettingsValues = {
  -readonly [K in keyof typeof DEFAULT_CLIENT_PORTAL_SETTINGS]: boolean
}

// =====================
// Granular per-user permissions + org templates
// =====================

/**
 * Tab-visibility flags. These describe the engagement scope of a client's
 * portal (which tabs exist) and act as a hard ceiling: a tab is visible only
 * when the client baseline allows it AND the per-user grant allows it. Set
 * both at the client level (Setup) and per-user.
 */
export const PORTAL_VISIBILITY_KEYS = [
  'showNotes',
  'showDocuments',
  'showDiscussions',
  'showSupport',
  'showDeployments',
  'showRetainers',
] as const

/**
 * Action-capability flags ("what a client can do"). These are now PER-USER
 * ONLY — there is no client-level cap. A capability is on purely when the
 * user's grant enables it. Derived from the `allow*` keys so it stays in sync
 * with DEFAULT_CLIENT_PORTAL_SETTINGS.
 */
export const PORTAL_ACTION_KEYS = Object.keys(
  DEFAULT_CLIENT_PORTAL_SETTINGS,
).filter(key => key.startsWith('allow')) as Array<
  Extract<keyof typeof DEFAULT_CLIENT_PORTAL_SETTINGS, `allow${string}`>
>

/**
 * Client-baseline content/display config — NOT per-user capabilities, never
 * bundled into grants or templates. `showLinkedDocuments` is the share-list
 * config for Documents & Links; the `showRetainer*` breakdown flags control
 * how a client's retainer data is presented. These pass through from the
 * baseline unchanged during effective-settings resolution.
 */
export const PORTAL_DISPLAY_CONFIG_KEYS = [
  'showLinkedDocuments',
  'showRetainerHistory',
  'showRetainerProjectBreakdown',
  'showRetainerMemberBreakdown',
  'showRetainerRoleBreakdown',
  'showRetainerOverageProjection',
] as const

/**
 * The portal capability flags that can be granted per-user and bundled into
 * org permission templates: tab visibility + actions. Excludes `isEnabled`
 * (the portal master switch) and all display-config keys (client-baseline
 * only). Grants are "hidden until granted", so adding a key here silently
 * turns it OFF for every existing portal user.
 */
export const PORTAL_PERMISSION_KEYS = [
  ...PORTAL_VISIBILITY_KEYS,
  ...PORTAL_ACTION_KEYS,
] as Array<
  Exclude<
    keyof typeof DEFAULT_CLIENT_PORTAL_SETTINGS,
    'isEnabled' | (typeof PORTAL_DISPLAY_CONFIG_KEYS)[number]
  >
>

export type PortalPermissionKey = (typeof PORTAL_PERMISSION_KEYS)[number]
export type PortalActionKey = (typeof PORTAL_ACTION_KEYS)[number]

/** Partial map of permission flags — used for per-user grants and templates. */
export type PortalPermissionMap = Partial<Record<PortalPermissionKey, boolean>>

const PORTAL_ACTION_KEY_SET = new Set<string>(PORTAL_ACTION_KEYS)

const PORTAL_PERMISSION_KEY_SET = new Set<string>(PORTAL_PERMISSION_KEYS)

export const PortalPermissionMapSchema = z
  .record(z.string(), z.boolean())
  .refine(
    map => Object.keys(map).every(key => PORTAL_PERMISSION_KEY_SET.has(key)),
    { message: 'Unknown portal permission key' },
  )
  .transform(map => map as PortalPermissionMap)

/**
 * Resolve a portal user's effective settings from the client baseline and the
 * user's per-user grant:
 *   - Tab visibility (PORTAL_VISIBILITY_KEYS): on only when the baseline allows
 *     it AND the user is granted it — the baseline is a hard ceiling.
 *   - Actions (PORTAL_ACTION_KEYS): PER-USER ONLY — on when the grant enables
 *     it, regardless of the baseline (the baseline no longer caps actions).
 *   - Everything else (`isEnabled`, display config): passes through unchanged.
 *
 * Internal viewers (org staff previewing) pass `grant = null/undefined`: they
 * get the full baseline for visibility/display plus all actions enabled, so
 * the preview is unconstrained. Shared by the API guard and the portal summary
 * endpoint so both agree on the resolution rule.
 */
export function resolveEffectivePortalSettings<
  T extends Partial<ClientPortalSettingsValues> & Record<string, boolean>,
>(baseline: T, grant: PortalPermissionMap | null | undefined): T {
  const effective = { ...baseline }
  // Internal viewer: full preview — keep baseline visibility/display, enable
  // every action (baseline no longer stores meaningful action values).
  if (!grant) {
    for (const key of PORTAL_ACTION_KEYS) {
      if (key in effective) {
        ;(effective as Record<string, boolean>)[key] = true
      }
    }
    return effective
  }
  for (const key of PORTAL_VISIBILITY_KEYS) {
    if (key in effective) {
      const allowed = (effective as Record<string, boolean>)[key] === true
      ;(effective as Record<string, boolean>)[key] =
        allowed && grant[key] === true
    }
  }
  for (const key of PORTAL_ACTION_KEYS) {
    if (key in effective) {
      ;(effective as Record<string, boolean>)[key] = grant[key] === true
    }
  }
  return effective
}

/** True when `key` is an action capability (per-user only, no baseline cap). */
export function isPortalActionKey(key: string): boolean {
  return PORTAL_ACTION_KEY_SET.has(key)
}

export interface ClientPortalPermissionTemplate {
  id: string
  organizationId: string
  name: string
  description?: string | null
  isSystem: boolean
  settings: PortalPermissionMap
  createdBy?: string | null
  createdAt?: string | Date
  updatedAt?: string | Date
}

export const CreateClientPortalPermissionTemplateSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  description: z.string().max(2000).nullable().optional(),
  settings: PortalPermissionMapSchema.default({}),
})

export type CreateClientPortalPermissionTemplateDTO = z.infer<
  typeof CreateClientPortalPermissionTemplateSchema
>

export const UpdateClientPortalPermissionTemplateSchema =
  CreateClientPortalPermissionTemplateSchema.partial()

export type UpdateClientPortalPermissionTemplateDTO = z.infer<
  typeof UpdateClientPortalPermissionTemplateSchema
>

export const UpdateClientPortalAccessPermissionsSchema = z.object({
  permissions: PortalPermissionMapSchema,
})

export type UpdateClientPortalAccessPermissionsDTO = z.infer<
  typeof UpdateClientPortalAccessPermissionsSchema
>

/**
 * Optional grant to set when reinstating a deactivated portal user. Pick a
 * template (resolved server-side) or pass a custom map; omit both to preserve
 * the user's existing grant.
 */
export const ReinstateClientPortalAccessSchema = z.object({
  templateId: z.string().uuid().nullable().optional(),
  permissions: PortalPermissionMapSchema.optional(),
})

export type ReinstateClientPortalAccessDTO = z.infer<
  typeof ReinstateClientPortalAccessSchema
>

export const UpdateClientPortalSettingsSchema = z
  .object({
    isEnabled: z.boolean().optional(),
    allowNoteEdits: z.boolean().optional(),
    allowNoteCreate: z.boolean().optional(),
    allowNoteDelete: z.boolean().optional(),
    allowThreadComments: z.boolean().optional(),
    allowThreadReactions: z.boolean().optional(),
    allowNoteFileUploads: z.boolean().optional(),
    allowThreadFileUploads: z.boolean().optional(),
    allowPortalDocumentUploads: z.boolean().optional(),
    allowPortalLinkUploads: z.boolean().optional(),
    allowPortalDocumentDeletes: z.boolean().optional(),
    allowPortalLinkDeletes: z.boolean().optional(),
    allowTicketReplies: z.boolean().optional(),
    allowTicketClose: z.boolean().optional(),
    allowTicketCreate: z.boolean().optional(),
    showNotes: z.boolean().optional(),
    showDocuments: z.boolean().optional(),
    showLinkedDocuments: z.boolean().optional(),
    showDiscussions: z.boolean().optional(),
    showSupport: z.boolean().optional(),
    showDeployments: z.boolean().optional(),
    showRetainers: z.boolean().optional(),
    showRetainerHistory: z.boolean().optional(),
    showRetainerProjectBreakdown: z.boolean().optional(),
    showRetainerMemberBreakdown: z.boolean().optional(),
    showRetainerRoleBreakdown: z.boolean().optional(),
    showRetainerOverageProjection: z.boolean().optional(),
  })
  .strict()

export type UpdateClientPortalSettingsDTO = z.infer<
  typeof UpdateClientPortalSettingsSchema
>

// DTO for a portal user creating a support ticket. Subject + description
// + priority. Due date, category, and attachments stay on the staff
// side until/unless we explicitly expose them.
export const CreatePortalTicketSchema = z.object({
  subject: z.string().min(1, 'Subject is required').max(200),
  description: z.string().max(50_000).optional(),
  priority: z.nativeEnum(TicketPriorityEnum).optional(),
  typeDefId: z.string().uuid().nullable().optional(),
  // Client-generated id used to scope inline-image uploads before the ticket
  // exists; the backend re-points those uploads onto the real ticket on create.
  draftId: z.string().uuid().optional(),
  // Files staged under `draftId` (skip-document uploads). Seeded onto an
  // initial inbound communication so they show in the conversation.
  attachments: z.array(TicketAttachmentInputSchema).max(25).optional(),
})

export type CreatePortalTicketDTO = z.infer<typeof CreatePortalTicketSchema>

// DTO for a portal user replying to a ticket. bodyText required; optional
// attachments staged under `replyDraftId` (a client-generated uuid scoping
// the skip-document uploads for this reply).
export const PortalTicketReplySchema = z.object({
  bodyText: z.string().min(1, 'Reply body is required'),
  bodyHtml: z.string().max(1_000_000).optional(),
  replyDraftId: z.string().uuid().optional(),
  attachments: z.array(TicketAttachmentInputSchema).max(25).optional(),
})

export type PortalTicketReplyDTO = z.infer<typeof PortalTicketReplySchema>

export const UpdateClientPortalSharesSchema = z.object({
  projectIds: z.array(z.string().uuid()).optional(),
  noteIds: z.array(z.string().uuid()).optional(),
  threadIds: z.array(z.string().uuid()).optional(),
  documentIds: z.array(z.string().uuid()).optional(),
  documentCategoryIds: z.array(z.string().uuid()).optional(),
})

export type UpdateClientPortalSharesDTO = z.infer<
  typeof UpdateClientPortalSharesSchema
>

// =====================
// Portal Documents & Links
// =====================

/**
 * A document folder surfaced on the portal Documents & Links page because it
 * (or an ancestor) was explicitly shared. `isSharedRoot` marks the directly
 * shared folders — the client UI groups documents under these roots.
 */
export interface PortalSharedDocumentCategory {
  id: string
  name: string
  icon?: string | null
  color?: string | null
  parentId: string | null
  isSharedRoot: boolean
}

/** Response shape of the portal Documents & Links listing endpoint. */
export interface PortalSharedDocumentsResponse {
  documents: DocumentResponse[]
  categories: PortalSharedDocumentCategory[]
}

// =====================
// Task Sharing Settings
// =====================

export const ClientPortalTaskVisibilityModes = [
  'ALL',
  'BY_GROUP',
  'BY_TAG',
] as const
export type ClientPortalTaskVisibilityMode =
  (typeof ClientPortalTaskVisibilityModes)[number]

// Default visible columns for client portal
export const DEFAULT_CLIENT_PORTAL_TASK_COLUMNS = [
  'name',
  'statusTypeId',
  'priority',
  'dueDate',
] as const

// Per-project task settings schema
export const ClientPortalProjectTaskSettingsSchema = z.object({
  taskVisibility: z.enum(ClientPortalTaskVisibilityModes).default('ALL'),
  visibleGroupIds: z.array(z.string().uuid()).default([]),
  visibleTagIds: z.array(z.string().uuid()).default([]),
  visibleColumns: z
    .array(z.string())
    .default([...DEFAULT_CLIENT_PORTAL_TASK_COLUMNS]),
  allowTaskComments: z.boolean().default(false),
  allowTaskDescriptionUpdate: z.boolean().default(false),
  allowTaskStatusUpdate: z.boolean().default(false),
  allowTaskPriorityUpdate: z.boolean().default(false),
  allowTaskDueDateUpdate: z.boolean().default(false),
  allowTaskCreate: z.boolean().default(false),
  creatableGroupIds: z.array(z.string().uuid()).default([]),
  // Whether client-created tasks can be assigned to a sprint, scoped by sprint status.
  // 'off' = no sprint assignment, 'planned' = PLANNED only, 'all' = PLANNED + ACTIVE.
  taskCreateInSprintMode: z.enum(['off', 'planned', 'all']).default('off'),
  // Write capability for task comments (allowTaskComments above controls visibility of the comments column)
  allowTaskCommentCreate: z.boolean().default(false),
  // Whether clients can edit their OWN task comments.
  allowTaskCommentEdit: z.boolean().default(false),
  // Whether clients can delete their OWN task comments.
  allowTaskCommentDelete: z.boolean().default(false),
  allowTaskFileAttachments: z.boolean().default(false),
  // Whether clients can drag-to-reorder tasks in the backlog / task list.
  // Writes the shared global Task.order, so staff and clients share one ordering.
  allowTaskReorder: z.boolean().default(false),
  // Whether clients can drag-to-reorder tasks within a sprint.
  allowSprintReorder: z.boolean().default(false),
  // Whether clients can move tasks between shared work sections (task groups,
  // sprints, cycles, buckets, and backlog) via the mass-action bar.
  // Independent of the reorder flags above, which only control position
  // within a container. The legacy name is retained for API compatibility.
  allowTaskSprintChange: z.boolean().default(false),
  showSubtasks: z.boolean().default(true),
  // Custom field definition IDs the portal user is allowed to edit inline.
  // Each custom field opts in individually via Project Settings → Client Portal.
  editableCustomFieldIds: z.array(z.string().uuid()).default([]),
  // Status IDs (TaskStatus.statusId strings, not uuids) that mean the task is
  // waiting on the CLIENT to act (e.g. a "Client Review" status). Tasks in
  // these statuses surface in the portal home "Waiting on you" widget. Empty
  // = the project contributes nothing to the widget.
  clientActionStatusIds: z.array(z.string()).default([]),
  // Per-task-type refinement of clientActionStatusIds, keyed by
  // ProjectTaskTypeDef id. A type with an entry here uses that list instead of
  // the flat clientActionStatusIds; types without an entry (and tasks with no
  // task type) fall back to the flat list. Also drives "ready for your review"
  // portal notifications when a task transitions into one of these statuses.
  clientActionStatusIdsByType: z
    .record(z.string(), z.array(z.string()))
    .default({}),
})

export type ClientPortalProjectTaskSettings = z.infer<
  typeof ClientPortalProjectTaskSettingsSchema
>

/**
 * Resolves the client-action status ids for a task of the given type: the
 * per-type entry wins when present, otherwise the legacy flat list applies.
 */
export function getEffectiveClientActionStatusIds(
  settings: Pick<
    ClientPortalProjectTaskSettings,
    'clientActionStatusIds' | 'clientActionStatusIdsByType'
  >,
  taskTypeDefId: string | null | undefined,
): string[] {
  if (taskTypeDefId) {
    const byType = settings.clientActionStatusIdsByType?.[taskTypeDefId]
    if (byType) return byType
  }
  return settings.clientActionStatusIds ?? []
}

export type SprintAccessMode = 'off' | 'planned' | 'all'

/**
 * Returns true when the given sprint access mode permits the action on a sprint
 * with the given status. COMPLETED and CANCELLED sprints are always blocked.
 */
export function isSprintAccessAllowed(
  mode: SprintAccessMode | undefined,
  sprintStatus: string | null | undefined,
): boolean {
  if (!mode || mode === 'off') return false
  if (sprintStatus !== 'PLANNED' && sprintStatus !== 'ACTIVE') return false
  if (mode === 'planned') return sprintStatus === 'PLANNED'
  return true
}

// DTO for updating project task settings
export const UpdateClientPortalProjectTaskSettingsSchema =
  ClientPortalProjectTaskSettingsSchema.partial()

export type UpdateClientPortalProjectTaskSettingsDTO = z.infer<
  typeof UpdateClientPortalProjectTaskSettingsSchema
>

// =====================
// Per-member project capability overrides
// =====================

/**
 * The "what can this client do" half of a project's task settings. These are
 * the only fields overridable per portal member — the visibility fields
 * (taskVisibility, visibleGroupIds, visibleTagIds, visibleColumns, showSubtasks)
 * stay project-wide and shared across every member. Mirrors the entity-level
 * PORTAL_ACTION_KEYS split, applied to project task settings.
 */
export const PROJECT_TASK_CAPABILITY_KEYS = [
  'allowTaskCreate',
  'creatableGroupIds',
  'taskCreateInSprintMode',
  'allowTaskDescriptionUpdate',
  'allowTaskStatusUpdate',
  'allowTaskPriorityUpdate',
  'allowTaskDueDateUpdate',
  'allowTaskComments',
  'allowTaskCommentCreate',
  'allowTaskCommentEdit',
  'allowTaskCommentDelete',
  'allowTaskFileAttachments',
  'allowTaskReorder',
  'allowSprintReorder',
  'allowTaskSprintChange',
  'editableCustomFieldIds',
] as const satisfies ReadonlyArray<keyof ClientPortalProjectTaskSettings>

export type ProjectTaskCapabilityKey =
  (typeof PROJECT_TASK_CAPABILITY_KEYS)[number]

/**
 * A sparse per-member override of a project's task capabilities. Only the keys
 * an admin customized for the member are present; absent keys inherit the
 * project default.
 */
export type ProjectTaskCapabilityMap = Partial<
  Pick<ClientPortalProjectTaskSettings, ProjectTaskCapabilityKey>
>

const PROJECT_TASK_CAPABILITY_KEY_SET = new Set<string>(
  PROJECT_TASK_CAPABILITY_KEYS,
)

/**
 * Validates a per-member capability override: a partial of the capability
 * fields, rejecting any visibility/unknown key so members can never widen or
 * narrow what tasks/columns they see (that stays project-wide).
 */
export const ProjectTaskCapabilityMapSchema =
  UpdateClientPortalProjectTaskSettingsSchema.refine(
    map =>
      Object.keys(map).every(key => PROJECT_TASK_CAPABILITY_KEY_SET.has(key)),
    { message: 'Only project task capability keys may be overridden per member' },
  ).transform(map => map as ProjectTaskCapabilityMap)

/**
 * Resolve a portal member's effective project task settings from the project
 * default (share.taskSettings) and their optional per-member override:
 *   - Capability keys (PROJECT_TASK_CAPABILITY_KEYS): replaced by the override
 *     when present — a sparse replace, not an AND-cap, so an admin can grant a
 *     member more or less than the default.
 *   - Visibility keys: always pass through from the default, never per-member.
 *
 * `override = null/undefined` (internal admin preview, or a member with no
 * override row) returns the default unchanged. Shared by the API guard/service
 * and admin UI so both agree on the resolution rule.
 */
export function resolveEffectiveProjectTaskSettings(
  projectDefault: ClientPortalProjectTaskSettings,
  override: ProjectTaskCapabilityMap | null | undefined,
): ClientPortalProjectTaskSettings {
  if (!override) return { ...projectDefault }
  const effective = { ...projectDefault }
  for (const key of PROJECT_TASK_CAPABILITY_KEYS) {
    const value = override[key]
    if (value !== undefined) {
      ;(effective as Record<string, unknown>)[key] = value
    }
  }
  return effective
}

/** True when `key` is a per-member-overridable project task capability. */
export function isProjectTaskCapabilityKey(key: string): boolean {
  return PROJECT_TASK_CAPABILITY_KEY_SET.has(key)
}

// =====================
// Staff-side portal visibility summary
// =====================

/**
 * Maps a built-in task column key to the settings boolean that grants clients
 * edit access to that column. Columns absent here (and non-custom) are always
 * view-only when visible.
 */
export const CLIENT_PORTAL_COLUMN_PERMISSION_MAP: Partial<
  Record<string, keyof ClientPortalProjectTaskSettings>
> = {
  statusTypeId: 'allowTaskStatusUpdate',
  priority: 'allowTaskPriorityUpdate',
  dueDate: 'allowTaskDueDateUpdate',
  description: 'allowTaskDescriptionUpdate',
}

export type ClientPortalColumnState = 'hidden' | 'view' | 'edit'

/**
 * Resolve a task column's client-facing state from a share's project-level
 * task settings. Pass `customFieldDefinitionId` only for `custom_*` keys so
 * editableCustomFieldIds can be consulted.
 */
export function getClientPortalColumnState(
  settings: ClientPortalProjectTaskSettings,
  columnKey: string,
  customFieldDefinitionId?: string,
): ClientPortalColumnState {
  if (!settings.visibleColumns.includes(columnKey)) return 'hidden'
  const permKey = CLIENT_PORTAL_COLUMN_PERMISSION_MAP[columnKey]
  if (permKey && Boolean(settings[permKey])) return 'edit'
  if (
    customFieldDefinitionId &&
    settings.editableCustomFieldIds.includes(customFieldDefinitionId)
  ) {
    return 'edit'
  }
  return 'view'
}

/**
 * Staff-side summary of what a project's published portal share exposes.
 * Response of GET /projects/:id/client-portal-visibility. Draft (hidden)
 * shares report shared=false — they are not visible to clients.
 */
export interface ProjectPortalVisibility {
  shared: boolean
  /** The client entity the share belongs to; null when not shared. */
  entityId: string | null
  /** Parsed project-level task settings; null when not shared. */
  taskSettings: ClientPortalProjectTaskSettings | null
  /** Names for the row-visibility summary; populated for BY_GROUP only. */
  visibleGroups: { id: string; name: string }[]
  /** Names for the row-visibility summary; populated for BY_TAG only. */
  visibleTags: { id: string; name: string }[]
}

// DTO for client creating a task
export const CreateClientPortalTaskSchema = z.object({
  name: z.string().min(1, 'Task name is required'),
  description: z.string().optional(),
  groupId: z.string().uuid('Group is required'),
  priority: z.string().optional(),
  taskTypeDefId: z.string().uuid().nullable().optional(),
  dueDate: z.string().optional(),
  sprintId: z.string().uuid().optional(),
  cycleInstanceId: z.string().uuid().optional(),
})

export type CreateClientPortalTaskDTO = z.infer<
  typeof CreateClientPortalTaskSchema
>

// DTO for client updating task status
export const UpdateClientPortalTaskStatusSchema = z.object({
  statusId: z.string().uuid(),
  // statusTypeId is a category like 'NOT_STARTED', 'IN_PROGRESS', 'DONE', not a UUID
  statusTypeId: z.string().optional(),
})

export type UpdateClientPortalTaskStatusDTO = z.infer<
  typeof UpdateClientPortalTaskStatusSchema
>

// DTO for client updating task priority
export const UpdateClientPortalTaskPrioritySchema = z.object({
  priority: z.enum(['urgent', 'high', 'medium', 'low', 'none']).nullable(),
})

export type UpdateClientPortalTaskPriorityDTO = z.infer<
  typeof UpdateClientPortalTaskPrioritySchema
>

// DTO for client updating task due date
export const UpdateClientPortalTaskDueDateSchema = z.object({
  dueDate: z.string().datetime().nullable(),
})

export type UpdateClientPortalTaskDueDateDTO = z.infer<
  typeof UpdateClientPortalTaskDueDateSchema
>

// Extended share schema including task settings
export const ClientPortalShareWithSettingsSchema =
  ClientPortalShareSchema.extend({
    taskSettings: ClientPortalProjectTaskSettingsSchema.nullable().optional(),
    settingsUpdatedAt: z.date().nullable().optional(),
  })

export type ClientPortalShareWithSettings = z.infer<
  typeof ClientPortalShareWithSettingsSchema
>
